import { type FC } from "react";
import {
  IconButton,
  ListItem,
  ListItemButton,
  ListItemText,
  Typography,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import { useTranslation } from "react-i18next";
import { ProductTypeType } from "@/types/graphql/partner.generated";
import { formatCurrency } from "@/partner/features/products";
import type { ProductFormPrice } from "../model";

const RECURRENT_PERIOD_KEYS: Record<number, string> = {
  7: "products.priceEditDialog.periodWeekly",
  30: "products.priceEditDialog.periodMonthly",
  90: "products.priceEditDialog.period3Month",
  180: "products.priceEditDialog.periodHalfYear",
  365: "products.priceEditDialog.periodYearly",
};

interface PriceRowProps {
  price: ProductFormPrice;
  productType: ProductTypeType;
  countryAccessTitleById: Record<string, string>;
  onEdit: () => void;
  onRemove: () => void;
}

/**
 * Строка списка цен продукта (референс: PricesField/SubscriptionRow.js +
 * TicketRow.js слиты в один компонент, различие — только заголовок строки:
 * период подписки словами vs «N дней» для разового билета).
 */
export const PriceRow: FC<PriceRowProps> = ({
  price,
  productType,
  countryAccessTitleById,
  onEdit,
  onRemove,
}) => {
  const { t } = useTranslation();
  const isSubscription = productType === ProductTypeType.SubscriptionType;

  const primary = isSubscription
    ? price.period && RECURRENT_PERIOD_KEYS[price.period]
      ? t(RECURRENT_PERIOD_KEYS[price.period])
      : ""
    : t("products.priceRow.periodDays", { count: price.period ?? 0 });

  const sortedPeriods = [...price.periods].sort((a, b) => {
    if (!a.dateFrom) return 1;
    if (!b.dateFrom) return -1;
    return new Date(a.dateFrom).getTime() - new Date(b.dateFrom).getTime();
  });

  return (
    // référence: TicketRow.js:68 — ListItem disablePadding БЕЗ disableGutters
    // (билет с гуттерами); SubscriptionRow.js:95 — disablePadding И
    // disableGutters (подписка без гуттеров).
    <ListItem disablePadding disableGutters={isSubscription}>
      <ListItemButton onClick={onEdit}>
        <ListItemText
          primary={primary}
          secondaryTypographyProps={{ component: "div" }}
          secondary={
            <>
              {sortedPeriods.map((period) => (
                <div key={period.key}>
                  <Typography component="span" sx={{ mr: 1 }}>
                    {period.dateFrom &&
                      t("products.priceRow.periodDateFromWithCost", {
                        date: new Date(period.dateFrom),
                        cost: formatCurrency(
                          Number(period.value) || 0,
                          price.currency
                        ),
                      })}
                  </Typography>
                  {period.oldValue && (
                    <Typography
                      component="span"
                      sx={{ textDecoration: "line-through", mr: 2 }}
                    >
                      {formatCurrency(Number(period.oldValue), price.currency)}
                    </Typography>
                  )}
                </div>
              ))}
              {isSubscription && Boolean(price.trialPeriod) && (
                <Typography>
                  {price.trialValue
                    ? t("products.priceRow.trialDaysWithCost", {
                        count: price.trialPeriod,
                        price: formatCurrency(
                          Number(price.trialValue),
                          price.currency
                        ),
                      })
                    : t("products.priceRow.trialDaysFree", {
                        count: price.trialPeriod,
                      })}
                </Typography>
              )}
              <Typography>
                {price.countryAccessId
                  ? t("products.priceRow.countryAccess", {
                      title: countryAccessTitleById[price.countryAccessId],
                    })
                  : t("products.priceRow.defaultCountryAccess")}
              </Typography>
            </>
          }
        />
        <IconButton
          onClick={(event) => {
            event.stopPropagation();
            onEdit();
          }}
          size="large"
        >
          <EditIcon />
        </IconButton>
        <IconButton
          onClick={(event) => {
            event.stopPropagation();
            onRemove();
          }}
          size="large"
        >
          <DeleteIcon />
        </IconButton>
      </ListItemButton>
    </ListItem>
  );
};
